import React, { useState } from "react";
import { Download, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { api, formatApiDetail } from "../../lib/api";
import {
  downloadPacketByFileId,
  downloadPacketByInvoiceId,
  sendPacket,
} from "../../lib/packetApi";

/**
 * Shared Send email + Download (+ Share for review) actions for scope/invoice packets.
 */
export default function PacketActions({
  fileId,
  invoiceId,
  clientId,
  toEmail,
  disabled = false,
  className = "",
  size = "default",
  onSent,
  onSharedReview,
}) {
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const canAct = Boolean(fileId || invoiceId) && !disabled;
  const canShareReview = Boolean(clientId && fileId) && !disabled;

  const handleSend = async () => {
    if (!canAct || sending) return;
    setSending(true);
    try {
      const data = await sendPacket({ fileId, invoiceId, toEmail });
      toast.success(
        data?.to_email
          ? `Packet emailed to ${data.to_email}`
          : data?.email_sent === false
            ? "Packet prepared (email delivery skipped — check RESEND_API_KEY)"
            : "Packet emailed",
      );
      onSent?.(data);
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not email packet."));
    } finally {
      setSending(false);
    }
  };

  const handleDownload = async () => {
    if (!canAct || downloading) return;
    setDownloading(true);
    try {
      if (invoiceId) {
        await downloadPacketByInvoiceId(invoiceId);
      } else {
        await downloadPacketByFileId(fileId);
      }
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(
        formatApiDetail(
          e.response?.data?.detail || e.message,
          "Could not download PDF.",
        ),
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleShareReview = async () => {
    if (!canShareReview || sharing) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/scope-review`, {
        packet_file_id: fileId,
        rotate_token: false,
      });
      const path =
        data?.share_path ||
        (data?.scope_review?.token ? `/scope/${data.scope_review.token}` : null);
      if (!path) throw new Error("No review link returned");
      const url = `${window.location.origin}${path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Review link copied — Accept / Request changes for the client");
      } catch {
        toast.message(url);
      }
      onSharedReview?.(data);
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not create review link."));
    } finally {
      setSharing(false);
    }
  };

  const btnClass =
    size === "sm"
      ? "!rounded-[var(--t-event-pill-radius)] h-8 px-2.5 text-xs"
      : "!rounded-[var(--t-event-pill-radius)] min-h-[40px]";

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="packet-actions">
      {canShareReview ? (
        <Button
          type="button"
          variant="default"
          className={`${btnClass} bg-[var(--t-accent)] text-white hover:bg-[var(--t-accent)]/90`}
          disabled={sharing}
          onClick={handleShareReview}
          data-testid="packet-share-review"
        >
          <Link2 size={14} strokeWidth={1.4} className="mr-1.5" />
          {sharing ? "Sharing…" : "Share for review"}
        </Button>
      ) : null}
      <Button
        type="button"
        variant={canShareReview ? "outline" : "default"}
        className={
          canShareReview
            ? btnClass
            : `${btnClass} bg-[var(--t-accent)] text-white hover:bg-[var(--t-accent)]/90`
        }
        disabled={!canAct || sending}
        onClick={handleSend}
        data-testid="packet-send-email"
      >
        <Mail size={14} strokeWidth={1.4} className="mr-1.5" />
        {sending ? "Sending…" : "Email PDF"}
      </Button>
      <Button
        type="button"
        variant="outline"
        className={btnClass}
        disabled={!canAct || downloading}
        onClick={handleDownload}
        data-testid="packet-download"
      >
        <Download size={14} strokeWidth={1.4} className="mr-1.5" />
        {downloading ? "Downloading…" : "Download"}
      </Button>
    </div>
  );
}
